import DownloadButton from "../DownloadButton";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/ListOfEmployeesLayout.css";

const pickValue = (source, keys, fallback = "") => {
  if (!source) return fallback;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const normalizeMaritalStatus = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "";
  }

  const normalized = String(value).trim().toLowerCase();
  if (["yes", "y", "true", "1", "married"].includes(normalized)) {
    return "Y";
  }
  if (["no", "n", "false", "0", "single", "unmarried"].includes(normalized)) {
    return "N";
  }
  return String(value);
};

const buildEmployeeRows = (itemDetails = [], headDetails = {}) => {
  const mappedRows = (Array.isArray(itemDetails) ? itemDetails : [])
    .map((item, index) => ({
      sl_no: index + 1,
      name: pickValue(item, [
        "name",
        "employee_name",
        "name_of_employee",
        "emp_name",
      ]),
      designation: pickValue(item, ["designation", "role", "position"]),
      education: pickValue(item, [
        "education",
        "qualification",
        "educational_qualification",
      ]),
      age: pickValue(item, ["age"]),
      experience: pickValue(item, [
        "no_of_years_exp",
        "years_of_experience",
        "experience",
        "exp_years",
        "exp",
      ]),
      married: normalizeMaritalStatus(
        pickValue(item, ["married", "married_yn", "marital_status"]),
      ),
      skill_work_experience: pickValue(item, [
        "skill_work_experience",
        "skill_or_work_experience",
        "skill",
        "work_experience",
        "skills",
      ]),
    }))
    .filter(
      (row) =>
        String(row.name || "").trim() !== "" ||
        String(row.designation || "").trim() !== "" ||
        String(row.education || "").trim() !== "" ||
        String(row.age || "").trim() !== "" ||
        String(row.experience || "").trim() !== "" ||
        String(row.married || "").trim() !== "" ||
        String(row.skill_work_experience || "").trim() !== "",
    );

  let rows = mappedRows;

  if (rows.length === 0) {
    const headRow = {
      sl_no: 1,
      name: pickValue(headDetails, [
        "name",
        "employee_name",
        "name_of_employee",
      ]),
      designation: pickValue(headDetails, ["designation", "role", "position"]),
      education: pickValue(headDetails, [
        "education",
        "qualification",
        "educational_qualification",
      ]),
      age: pickValue(headDetails, ["age"]),
      experience: pickValue(headDetails, [
        "no_of_years_exp",
        "years_of_experience",
        "experience",
        "exp_years",
        "exp",
      ]),
      married: normalizeMaritalStatus(
        pickValue(headDetails, ["married", "married_yn", "marital_status"]),
      ),
      skill_work_experience: pickValue(headDetails, [
        "skill_work_experience",
        "skill_or_work_experience",
        "skill",
        "work_experience",
        "skills",
      ]),
    };

    const hasHeadData =
      String(headRow.name || "").trim() !== "" ||
      String(headRow.designation || "").trim() !== "" ||
      String(headRow.education || "").trim() !== "" ||
      String(headRow.age || "").trim() !== "" ||
      String(headRow.experience || "").trim() !== "" ||
      String(headRow.married || "").trim() !== "" ||
      String(headRow.skill_work_experience || "").trim() !== "";

    rows = hasHeadData ? [headRow] : [];
  }

  if (rows.length === 0) {
    rows = [
      {
        sl_no: 1,
        name: "",
        designation: "",
        education: "",
        age: "",
        experience: "",
        married: "",
        skill_work_experience: "",
      },
    ];
  }

  return rows;
};

export const ListOfEmployeesLayout = ({
  headDetails = {},
  companyData = {},
  itemDetails = [],
}) => {
  let organisationData = {};
  try {
    organisationData = JSON.parse(localStorage.getItem("organisation") || "{}");
  } catch (error) {
    organisationData = {};
  }

  const refNo =
    pickValue(headDetails, ["ref_no", "reference_no", "form_ref_no"]) ||
    "FP/F/TRG/01";
  const revNo = pickValue(headDetails, [
    "rev_no",
    "revision_no",
    "form_rev_no",
  ]);
  const revDate = formatDate(
    pickValue(headDetails, ["rev_date", "revision_date", "form_rev_date"]),
  );

  const employeeRows = buildEmployeeRows(itemDetails, headDetails);

  const tableData = {
    headings: [
      { display_name: "SL.NO", field_name: "sl_no", is_internal: false },
      { display_name: "NAME", field_name: "name", is_internal: false },
      {
        display_name: "DESIGNATION",
        field_name: "designation",
        is_internal: false,
      },
      {
        display_name: "EDUCATION",
        field_name: "education",
        is_internal: false,
      },
      { display_name: "AGE", field_name: "age", is_internal: false },
      {
        display_name: "NO. OF YEARS EXP",
        field_name: "experience",
        is_internal: false,
      },
      {
        display_name: "MARRIED Y/N",
        field_name: "married",
        is_internal: false,
      },
      {
        display_name: "SKILL / WORK EXPERIENCE",
        field_name: "skill_work_experience",
        is_internal: false,
      },
    ],
    rows: employeeRows,
  };

  return (
    <div className="list-of-employees-page">
      <div className="list-of-employees-card">
        <div className="list-of-employees-header-row">
          <div className="list-of-employees-brand">
            <img
              src={organisationData?.logo_url || ProfilePlaceholder}
              alt="Company Logo"
              className="list-of-employees-logo"
            />
            <div className="list-of-employees-org-name">
              {organisationData?.name || companyData?.company_name || ""}
            </div>
          </div>
          <div className="list-of-employees-title-box">LIST OF EMPLOYEES</div>
          <div className="list-of-employees-ref-box">
            <div>Ref. No. : {refNo || "-"}</div>
            <div>Rev. No. : {revNo || "-"}</div>
            <div>Rev. Date : {revDate || "-"}</div>
          </div>
        </div>

        <div className="list-of-employees-content-block">
          <table className="list-of-employees-table">
            <thead>
              <tr>
                <th className="sl-col">Sl.No.</th>
                <th className="name-col">Name</th>
                <th className="designation-col">Designation</th>
                <th className="education-col">Education</th>
                <th className="age-col">Age</th>
                <th className="exp-col">No. of years Exp</th>
                <th className="married-col">Married Y/N</th>
                <th className="skill-col">Skill / Work Experience</th>
              </tr>
            </thead>
            <tbody>
              {employeeRows.map((row) => (
                <tr key={`employee-row-${row.sl_no}`}>
                  <td className="sl-col">{row.sl_no}</td>
                  <td className="name-col">{row.name}</td>
                  <td className="designation-col">{row.designation}</td>
                  <td className="education-col">{row.education}</td>
                  <td className="age-col">{row.age}</td>
                  <td className="exp-col">{row.experience}</td>
                  <td className="married-col">{row.married}</td>
                  <td className="skill-col">{row.skill_work_experience}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="list-of-employees-sign-row">
            <div>
              Prepared By:{" "}
              {pickValue(headDetails, ["prepared_by", "created_by"])}
            </div>
            <div>Approved By: {pickValue(headDetails, ["approved_by"])}</div>
          </div>
        </div>

        <p className="invoice-system-generated-note">
          This is a system-generated document and does not require a signature.
        </p>

        <div className="invoice-buttons list-of-employees-actions">
          <DownloadButton
            tableData={tableData}
            fileName="hr-list-of-employees"
            downloadFormats={["pdf", "full-page-pdf"]}
            fullPageDownload={true}
            fullPageSelector=".list-of-employees-page"
          />
        </div>
      </div>
    </div>
  );
};
